import { useTrendSeries } from '@liza/ui/hooks/useTrendSeries';
import { cn } from '@liza/ui/utils/cn';
import type { WithId } from '@medplum/core';
import type { Observation } from '@medplum/fhirtypes';
import { Line, LineChart, ResponsiveContainer, YAxis } from 'recharts';

type Props = {
  results: WithId<Observation>[];
  className?: string;
  /** Line color (any CSS color); defaults to the primary token. */
  color?: string;
  /**
   * Animate the line draw. Turn off while the timeline is scrubbing/playing — otherwise the sparkline
   * re-draws from scratch on every reveal step, obscuring the latest point. Defaults to on.
   */
  animate?: boolean;
};

/**
 * Compact, axis-less sparkline for a measurement's recent readings — used in CarePlan item rows.
 * For the large, labeled chart in the detail overlay see `CarePlanMeasurementChart`.
 */
export const TrendChart = ({
  results,
  className,
  color = 'var(--color-primary)',
  animate = true,
}: Props): React.ReactNode => {
  const { data, hasSecondLine } = useTrendSeries(results);

  if (data.length < 3) return null;

  return (
    <div className={cn('h-12 w-30', className)}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
          <YAxis hide domain={['dataMin', 'dataMax']} />
          <Line
            type="monotone"
            dataKey="y"
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
            dot={false}
            activeDot={false}
            isAnimationActive={animate}
          />
          {hasSecondLine && (
            <Line
              type="monotone"
              dataKey="y2"
              stroke={color}
              strokeWidth={2}
              strokeLinecap="round"
              strokeOpacity={0.5}
              dot={false}
              activeDot={false}
              isAnimationActive={animate}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
